import { Alert, Card, CardContent, Chip, Grid, IconButton, Menu, MenuItem, Snackbar, Typography } from '@mui/material';
import { ContentCopy, Delete, Edit, MoreVert, OpenInNew, Unarchive } from '@mui/icons-material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyedMutator } from 'swr/_internal';
import { AccountData, AccountService, StorefrontData, StorefrontStatus } from '../../api';
import ConfirmationDialog from '../../common/ui/ConfimationDialog';
import { useSafeExecute } from '../../common/apiNotifications/safeExecute';
import { WorkshopCircle } from '../../common/ui/WorkshopCircle';
import { useLanguage } from '../../common/GeneralUtils';
import { getPassOrderPage } from '../../utils/AccountUtils';

type StorefrontCardProps = {
  storefront: StorefrontData;
  account: AccountData;
  onEdit: () => void;
  updateLocalAccount: KeyedMutator<AccountData>;
};

export const StorefrontCard = ({ storefront, account, onEdit, updateLocalAccount }: StorefrontCardProps) => {
  const { t } = useTranslation();
  const language = useLanguage();
  const safeExecute = useSafeExecute();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);

  const shortUrl = storefront.short_code ? `${import.meta.env.VITE_REACT_APP_LINKS_HOST}/${storefront.short_code}` : '';
  const reviewsCount = storefront.reviews?.length || 0;
  const faqsCount = storefront.faqs?.length || 0;
  const isArchived = storefront.status === StorefrontStatus.ARCHIVED;
  const activityLabel = account?.activity_label_plural?.[language] || t('storefronts.order_pages');

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCopyLink = async () => {
    handleMenuClose();
    if (shortUrl) {
      await navigator.clipboard.writeText(shortUrl);
      setCopySnackbarOpen(true);
    }
  };

  const handleArchiveOrUnarchive = async (confirmed: boolean) => {
    setArchiveDialogOpen(false);
    if (confirmed) {
      if (isArchived) {
        const updatedAccount = await safeExecute({
          apiCall: () => AccountService.restoreStorefront(storefront.id),
          successMessage: t('storefronts.unarchive_success'),
        });
        await updateLocalAccount(updatedAccount);
      } else {
        const updatedAccount = await safeExecute({
          apiCall: () => AccountService.deleteStorefront(storefront.id),
          successMessage: t('storefronts.archive_success'),
        });
        await updateLocalAccount(updatedAccount);
      }
    }
  };

  return (
    <>
      <Card>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Grid container justifyContent="space-between" alignItems="flex-start" mb={1}>
            <Grid item xs={10}>
              <Typography variant="h6" fontWeight="600">
                {storefront.name || `Storefront`}
              </Typography>
            </Grid>
            <Grid item>
              <IconButton size="small" onClick={handleMenuOpen}>
                <MoreVert />
              </IconButton>
            </Grid>
          </Grid>

          <Grid container gap={1} mb={1}>
            <Chip size="small" label={`${reviewsCount} ${t('storefronts.reviews')}`} variant="outlined" />
            <Chip size="small" label={`${faqsCount} ${t('storefronts.faqs')}`} variant="outlined" />
          </Grid>

          {storefront.order_page_ids && storefront.order_page_ids.length > 0 && (
            <Grid container gap={0.5} mb={1} alignItems="center" minHeight={24}>
              <Typography variant="caption" color="text.secondary" sx={{ marginInlineEnd: 0.5 }}>
                {activityLabel}:
              </Typography>
              {storefront.order_page_ids.map((orderPageId) => (
                <WorkshopCircle
                  key={orderPageId}
                  account={account}
                  orderPageId={orderPageId}
                  language={language}
                  withTooltip
                />
              ))}
            </Grid>
          )}

          {storefront.trial_order_page_ids && storefront.trial_order_page_ids.length > 0 && (
            <Grid container gap={0.5} mb={1} alignItems="center" minHeight={24}>
              <Typography variant="caption" color="text.secondary" sx={{ marginInlineEnd: 0.5 }}>
                {t('storefronts.trial_order_pages')}:
              </Typography>
              {storefront.trial_order_page_ids.map((orderPageId) => (
                <WorkshopCircle
                  key={orderPageId}
                  account={account}
                  orderPageId={orderPageId}
                  language={language}
                  withTooltip
                />
              ))}
            </Grid>
          )}

          <Grid container gap={0.5} mb={1} alignItems="center" minHeight={24}>
            {storefront.pass_order_page_ids?.map((id) => {
              const passOrderPage = getPassOrderPage(account, id);
              return passOrderPage ? (
                <Chip key={id} size="small" label={passOrderPage.label[language]} variant="outlined" />
              ) : null;
            })}
          </Grid>

          {shortUrl && (
            <Grid container alignItems="center" gap={1}>
              <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all', flex: 1 }}>
                {shortUrl}
              </Typography>
              <IconButton size="small" onClick={handleCopyLink}>
                <ContentCopy fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => window.open(shortUrl, '_blank')}>
                <OpenInNew fontSize="small" />
              </IconButton>
            </Grid>
          )}
        </CardContent>
      </Card>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            onEdit();
          }}>
          <Edit fontSize="small" sx={{ marginInlineEnd: 1 }} />
          {t('storefronts.edit')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            setArchiveDialogOpen(true);
          }}>
          {isArchived ? (
            <Unarchive fontSize="small" sx={{ marginInlineEnd: 1 }} />
          ) : (
            <Delete fontSize="small" sx={{ marginInlineEnd: 1 }} />
          )}
          {isArchived ? t('storefronts.unarchive') : t('storefronts.archive')}
        </MenuItem>
        <MenuItem onClick={handleCopyLink}>
          <ContentCopy fontSize="small" sx={{ marginInlineEnd: 1 }} />
          {t('storefronts.copy_link')}
        </MenuItem>
      </Menu>

      <ConfirmationDialog
        isOpen={archiveDialogOpen}
        onClose={handleArchiveOrUnarchive}
        title={isArchived ? t('storefronts.unarchive_confirmation_title') : t('storefronts.archive_confirmation_title')}
        content={
          isArchived ? t('storefronts.unarchive_confirmation_content') : t('storefronts.archive_confirmation_content')
        }
        ctaText={isArchived ? t('storefronts.unarchive') : t('storefronts.archive')}
        confirmButtonColor={isArchived ? 'primary' : 'error'}
      />

      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={2000}
        onClose={() => setCopySnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setCopySnackbarOpen(false)}>
          {t('storefronts.link_copied')}
        </Alert>
      </Snackbar>
    </>
  );
};
